/**
 * MUI theme factory for WES Hub 360 — light / dark (Entrega 1: auth + global foundation).
 */
import { createTheme } from '@mui/material/styles';
import {
  colors,
  colorsDark,
  typography,
  borderRadius,
  shadows,
  shadowsDark,
  transitions,
} from '../styles/designTokens';

export function buildWesHubTheme(mode) {
  const isDark = mode === 'dark';
  const c = isDark ? colorsDark : colors;
  const s = isDark ? shadowsDark : shadows;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
        contrastText: colors.neutral.white,
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
        contrastText: colors.neutral.white,
      },
      error: {
        main: colors.semantic.error,
        light: colors.semantic.error,
        dark: colors.semantic.danger,
      },
      warning: {
        main: colors.semantic.warning,
      },
      success: {
        main: colors.semantic.successSolid,
        contrastText: colors.neutral.white,
      },
      info: {
        main: colors.semantic.info,
      },
      background: {
        default: c.background.main,
        paper: c.background.paper,
      },
      text: {
        primary: c.text.primary,
        secondary: c.text.secondary,
        disabled: c.text.disabled,
      },
      divider: c.border.divider,
      action: {
        hover: isDark ? 'rgba(56, 188, 255, 0.08)' : 'rgba(1, 111, 244, 0.04)',
        selected: isDark ? 'rgba(1, 111, 244, 0.16)' : 'rgba(1, 111, 244, 0.08)',
        focus: isDark ? 'rgba(56, 188, 255, 0.2)' : 'rgba(1, 111, 244, 0.12)',
      },
    },
    shape: {
      borderRadius: borderRadius.md,
    },
    shadows: [
      s.none,
      s.sm,
      s.base,
      s.md,
      s.md,
      s.lg,
      s.lg,
      s.lg,
      s.xl,
      s.xl,
      s.xl,
      s.xl,
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
      s['2xl'],
    ],
    typography: {
      // Aligned to the Material Design scale used by joao-1wes/dashboard-static
      // (--md-*-* tokens in dashboardShell.css) so MUI Typography and raw
      // dashboard-static markup render at the same sizes side by side.
      fontFamily: typography.fontFamily.primary,
      fontSize: 16, // body-large — same as dashboard-static `body`
      fontWeightLight: typography.fontWeight.light,
      fontWeightRegular: typography.fontWeight.regular,
      fontWeightMedium: typography.fontWeight.medium,
      fontWeightBold: typography.fontWeight.bold,
      // display-small / headline-large / headline-medium / headline-small
      h1: {
        fontSize: '36px',
        fontWeight: typography.fontWeight.bold,
        lineHeight: 1.15,
        letterSpacing: typography.letterSpacing.tight,
      },
      h2: {
        fontSize: '32px',
        fontWeight: typography.fontWeight.bold,
        lineHeight: 1.2,
        letterSpacing: typography.letterSpacing.tight,
      },
      h3: {
        fontSize: '28px',
        fontWeight: typography.fontWeight.semibold,
        lineHeight: 1.25,
      },
      h4: {
        fontSize: '24px',
        fontWeight: typography.fontWeight.semibold,
        lineHeight: 1.3,
      },
      // title-large, title-medium
      h5: {
        fontSize: '22px',
        fontWeight: typography.fontWeight.semibold,
        lineHeight: 1.3,
      },
      h6: {
        fontSize: '16px',
        fontWeight: typography.fontWeight.semibold,
        lineHeight: 1.4,
      },
      // body-large, body-medium
      body1: {
        fontSize: '16px',
        lineHeight: '24px',
      },
      body2: {
        fontSize: '14px',
        lineHeight: '20px',
      },
      // label-small
      caption: {
        fontSize: '11px',
        lineHeight: 1.4,
      },
      button: {
        fontWeight: typography.fontWeight.medium,
        textTransform: 'none',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.background.main,
            scrollbarWidth: 'thin',
            scrollbarColor: isDark
              ? `${c.neutral.gray[400]} ${c.neutral.gray[100]}`
              : `${colors.neutral.gray[400]} ${colors.neutral.gray[100]}`,
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: c.neutral.gray[100],
            },
            '&::-webkit-scrollbar-thumb': {
              background: c.neutral.gray[400],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: c.neutral.gray[500],
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            textTransform: 'none',
            fontWeight: typography.fontWeight.medium,
            transition: `all ${transitions.duration.base} ${transitions.easing.easeInOut}`,
          },
          contained: {
            boxShadow: s.sm,
            '&:hover': {
              boxShadow: s.md,
            },
          },
          containedPrimary: {
            background: colors.primary.gradient,
            '&:hover': {
              background: colors.primary.gradient,
              filter: 'brightness(1.05)',
            },
          },
          outlinedPrimary: {
            borderColor: colors.primary.main,
            '&:hover': {
              borderColor: colors.primary.dark,
              backgroundColor: isDark ? 'rgba(56, 188, 255, 0.08)' : 'rgba(1, 111, 244, 0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: s.card,
            borderRadius: borderRadius.lg,
            border: `1px solid ${c.border.light}`,
            backgroundImage: 'none',
            backgroundColor: c.background.paper,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: c.background.paper,
          },
          elevation1: {
            boxShadow: s.sm,
          },
          elevation2: {
            boxShadow: s.base,
          },
          elevation3: {
            boxShadow: s.md,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: c.background.content,
            color: c.text.primary,
            borderBottom: `1px solid ${c.border.divider}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: c.background.sidebar,
            borderRight: `1px solid ${c.border.divider}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: `${borderRadius.lg}px 0 0 ${borderRadius.lg}px`,
            marginRight: 0,
            '&.Mui-selected': {
              background: colors.primary.gradient,
              color: colors.neutral.white,
              '&:hover': {
                background: colors.primary.gradient,
                filter: 'brightness(1.05)',
              },
              '& .MuiListItemIcon-root': {
                color: colors.neutral.white,
              },
              '& .MuiListItemText-primary': {
                color: colors.neutral.white,
                fontWeight: typography.fontWeight.medium,
              },
            },
            '&:hover': {
              backgroundColor: isDark ? 'rgba(56, 188, 255, 0.08)' : 'rgba(1, 111, 244, 0.04)',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: colors.primary.main,
            minWidth: 40,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: colors.primary.main,
            fontWeight: typography.fontWeight.regular,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: borderRadius.md,
              '& fieldset': {
                borderColor: c.border.default,
              },
              '&:hover fieldset': {
                borderColor: colors.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary.main,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.md,
          },
          colorPrimary: {
            background: c.accent.iconBg,
            color: colors.primary.main,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.primary.dark,
            fontSize: typography.fontSize.sm,
            borderRadius: borderRadius.md,
            color: colors.neutral.white,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
          },
          standardError: {
            backgroundColor: c.accent.pinkLight,
            color: colors.semantic.danger,
          },
          standardSuccess: {
            backgroundColor: c.accent.iconBg,
            color: colors.primary.main,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: c.background.sidebar,
              fontWeight: typography.fontWeight.semibold,
              color: c.text.primary,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: c.accent.iconBg,
            },
          },
        },
      },
    },
  });
}
